/**
 * Ad performance tracking and analytics service
 */

export interface AdAnalyticsEvent {
  type: 'impression' | 'click' | 'error' | 'load';
  adType: 'treasure-card' | 'sidebar';
  position?: number;
  timestamp: number;
  error?: string;
  loadTime?: number;
}

export interface AdAnalyticsMetrics {
  totalImpressions: number;
  totalClicks: number;
  totalErrors: number;
  averageLoadTime: number;
  clickThroughRate: number;
  errorRate: number;
  impressionsByType: Record<string, number>;
  clicksByType: Record<string, number>;
  errorsByType: Record<string, number>;
}

class AdAnalyticsService {
  private events: AdAnalyticsEvent[] = [];
  private maxEvents = 1000; // Keep last 1000 events

  /**
   * Track an ad event
   */
  trackEvent(event: Omit<AdAnalyticsEvent, 'timestamp'>): void {
    const fullEvent: AdAnalyticsEvent = {
      ...event,
      timestamp: Date.now()
    };

    this.events.push(fullEvent);

    // Keep only the most recent events
    if (this.events.length > this.maxEvents) {
      this.events = this.events.slice(-this.maxEvents);
    }

    // Log event in development
    if (import.meta.env.DEV) {
      console.log('Ad Analytics:', fullEvent);
    }

    // Send to external analytics if configured
    this.sendToExternalAnalytics(fullEvent);
  }

  /**
   * Track ad impression
   */
  trackImpression(adType: 'treasure-card' | 'sidebar', position?: number): void {
    this.trackEvent({
      type: 'impression',
      adType,
      position
    });
  }

  /**
   * Track ad click
   */
  trackClick(adType: 'treasure-card' | 'sidebar', position?: number): void {
    this.trackEvent({
      type: 'click',
      adType,
      position
    });
  }

  /**
   * Track ad error
   */
  trackError(adType: 'treasure-card' | 'sidebar', error: string, position?: number): void {
    this.trackEvent({
      type: 'error',
      adType,
      error,
      position
    });
  }

  /**
   * Track ad load time
   */
  trackLoad(adType: 'treasure-card' | 'sidebar', loadTime: number, position?: number): void {
    this.trackEvent({
      type: 'load',
      adType,
      loadTime,
      position
    });
  }

  /**
   * Get analytics metrics
   */
  getMetrics(): AdAnalyticsMetrics {
    const impressions = this.events.filter(e => e.type === 'impression');
    const clicks = this.events.filter(e => e.type === 'click');
    const errors = this.events.filter(e => e.type === 'error');
    const loads = this.events.filter(e => e.type === 'load');

    const totalImpressions = impressions.length;
    const totalClicks = clicks.length;
    const totalErrors = errors.length;

    const loadTimes = loads.filter(e => e.loadTime).map(e => e.loadTime!);
    const averageLoadTime = loadTimes.length > 0 
      ? loadTimes.reduce((sum, time) => sum + time, 0) / loadTimes.length 
      : 0;

    const clickThroughRate = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
    const errorRate = totalImpressions > 0 ? (totalErrors / totalImpressions) * 100 : 0;

    // Group by ad type
    const impressionsByType = this.groupEventsByType(impressions);
    const clicksByType = this.groupEventsByType(clicks);
    const errorsByType = this.groupEventsByType(errors);

    return {
      totalImpressions,
      totalClicks,
      totalErrors,
      averageLoadTime,
      clickThroughRate,
      errorRate,
      impressionsByType,
      clicksByType,
      errorsByType
    };
  }

  /**
   * Group events by ad type
   */
  private groupEventsByType(events: AdAnalyticsEvent[]): Record<string, number> {
    return events.reduce((acc, event) => {
      acc[event.adType] = (acc[event.adType] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }

  /**
   * Get events within a time range
   */
  getEventsInRange(startTime: number, endTime: number): AdAnalyticsEvent[] {
    return this.events.filter(event => 
      event.timestamp >= startTime && event.timestamp <= endTime
    );
  }

  /**
   * Get events for the last N hours
   */
  getRecentEvents(hours: number = 24): AdAnalyticsEvent[] {
    const startTime = Date.now() - (hours * 60 * 60 * 1000);
    return this.getEventsInRange(startTime, Date.now());
  }

  /**
   * Clear all events
   */
  clearEvents(): void {
    this.events = [];
  }

  /**
   * Export events as JSON
   */
  exportEvents(): string {
    return JSON.stringify(this.events, null, 2);
  }

  /**
   * Send event to external analytics service
   */
  private sendToExternalAnalytics(event: AdAnalyticsEvent): void {
    // In a real implementation, you would send this to Google Analytics,
    // Adobe Analytics, or another analytics service
    
    // Example: Google Analytics 4 event
    if (typeof (window as any).gtag !== 'undefined') {
      // @ts-ignore - gtag is loaded externally
      (window as any).gtag('event', `ad_${event.type}`, {
        ad_type: event.adType,
        ad_position: event.position,
        load_time: event.loadTime,
        error_message: event.error
      });
    }

    // Example: Custom analytics endpoint
    if (import.meta.env.VITE_ANALYTICS_ENDPOINT) {
      fetch(import.meta.env.VITE_ANALYTICS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(event)
      }).catch(error => {
        console.warn('Failed to send analytics event:', error);
      });
    }
  }
}

// Export singleton instance
export const adAnalyticsService = new AdAnalyticsService();

// Export types (already exported as interfaces above)